import React from "react";
import {
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  Controller,
  FieldValues,
  Control,
  UseFormWatch,
} from "react-hook-form";
import { LocalOffer } from "@mui/icons-material";

interface SettingsOptionProps {
  name: keyof FieldValues;
  label: string;
  thresholdName?: string;
  options: { value: string; label: string }[];
  control: Control<FieldValues>;
  watch: UseFormWatch<FieldValues>;
  translate: (_key: string) => string;
  allowedValues?: string[];
}

export const SettingsOption: React.FC<SettingsOptionProps> = ({
  name,
  label,
  options,
  control,
  watch,
  thresholdName,
  translate,
  allowedValues = [],
}) => {
  const isThresholdVisible = !allowedValues.includes(watch(name));

  return (
    <Box
      display="flex"
      flexDirection={{ xs: "column", sm: "row" }}
      alignItems="center"
      mb={2}
      gap={2}
    >
      <Typography
        variant="body2"
        fontWeight="bold"
        sx={{ width: { xs: "100%", sm: "20%" }, mb: { xs: 1, sm: 0 } }}
      >
        {translate(label)}:
      </Typography>
      <Box sx={{ width: { xs: "100%", sm: "80%" } }}>
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <RadioGroup {...field} sx={{ width: "100%" }}>
              {options.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio />}
                  label={translate(option.label)}
                />
              ))}
            </RadioGroup>
          )}
        />

        {thresholdName && isThresholdVisible && (
          <Controller
            name={thresholdName}
            control={control}
            defaultValue={0}
            rules={{
              validate: {
                required: (v, f) => !allowedValues.includes(f[name]),
                min: (v, f) =>
                  (!allowedValues.includes(f[name]) && v > 0) ||
                  translate("valueError"),
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <>
                <TextField
                  type="number"
                  label={translate(thresholdName + ".label")}
                  {...field}
                  size="small"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocalOffer fontSize="small" />
                        </InputAdornment>
                      ),
                    },
                  }}
                  required={isThresholdVisible}
                  error={!!error}
                  helperText={error ? error.message : ""}
                  sx={{ mt: 2 }}
                  onChange={(e) => {
                    const value = e.target.value;
                    const numericValue = value === "" ? "" : Number(value);
                    field.onChange(numericValue);
                  }}
                />
                {thresholdName === "maxDeliveryDateThreshold" && (
                  <Typography mt={1} color="warning">
                    {translate(thresholdName + ".note")}
                  </Typography>
                )}
              </>
            )}
          />
        )}
      </Box>
    </Box>
  );
};
